import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import router from "../router/index.js";

/***
 * STORE MODULOS
 * */
 import modSeguridad from "./modSeguridad/index";

import axios from "axios";
import { isDemoUser, maskDemoData } from "../utils/demoMask";

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.VUE_APP_API_URL;
Vue.use(Vuex);

export default new Vuex.Store({
  plugins: [createPersistedState()],
  state: {
    user: null,
    errors: null,
    mensaje_login: null,
    auth: false,
    permissions: [],
    menus: [],
    routes: [],
    accessToken: "",
    darkMode: false,
  },
  getters: {
    isAuthenticated: (state) => {
      return state.auth;
    },

    getUser: (state) => {
      return state.user || null;
    },

    getErrors: (state) => {
      return state.errors;
    },

    getPermissions: (state) => {
      return state.permissions;
    },

    getMensajeLogin: (state) => {
      return state.mensaje_login;
    },

    can: (state) => (permissionName) => {
      return (
        state.permissions.find((per) => per.name === permissionName) !==
        undefined
      );
    },

    getRoutes: (state) => {
      return state.routes;
    },

    getMenus: (state) => {
      return state.menus;
    },

    getDarkMode: (state) => {
      return state.darkMode;
    },

    isDemoUser: (state) => {
      return state.user && state.user.id === 78;
    },
  },
  mutations: {
    SET_USER(state, user) {
      if (user && isDemoUser(user)) {
        maskDemoData(user, user);
      }
      state.user = user;
      state.auth = Boolean(user);
    },

    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions;
    },

    SET_ERRORS(state, error) {
      state.errors = error;
    },

    SET_MENUS(state, menus) {
      state.menus = menus;
    },

    SET_ROUTES(state, routes) {
      state.routes = routes;
    },

    SET_DARK_MODE(state, darkMode) {
      state.darkMode = darkMode;
    },
    
    SET_MENSAJE_LOGIN(state, mensaje) {
      state.mensaje_login = mensaje;
    },
  },
  actions: {
    /**
     * Paso 1 login web: credenciales.
     * Si el backend responde requires_2fa, guarda pending en sessionStorage
     * y no carga usuario/sesión hasta verify2fa.
     */
    async login({ dispatch, commit }, credentials) {
      await axios.get("/sanctum/csrf-cookie");
      try {
        const res = await axios.post("/login", credentials);

        if (res.data?.requires_2fa) {
          const pending = {
            challenge_id: res.data.challenge_id,
            email_masked: res.data.email_masked,
            expires_in: res.data.expires_in,
            resend_after: res.data.resend_after,
          };
          sessionStorage.setItem("pending2fa", JSON.stringify(pending));
          return { requires_2fa: true, ...pending };
        }

        await dispatch("setDarkMode", false);
        await dispatch("getUser");
        return { requires_2fa: false };
      } catch (error) {
        let res = Object.assign({}, error);
        let err_recaptcha = res.response?.data?.errors?.recaptcha;
        let err_status = res.response?.data?.errors?.status;
        let err_estado_empresa = res.response?.data?.errors?.estado_empresa;
        let err_email = res.response?.data?.errors?.email;
        let err_destiempo = res.response?.data?.errors?.destiempo;
        if (res.response?.data?.error === "password_expired") {
          commit("SET_ERRORS", "password_expired");
        }
        if (typeof err_recaptcha !== "undefined") {
          commit("SET_ERRORS", "recaptcha");
        }
        if (typeof err_status !== "undefined") {
          commit("SET_ERRORS", "status");
        }
        if (typeof err_estado_empresa !== "undefined") {
          commit("SET_ERRORS", "estado_empresa");
        }
        if (typeof err_email !== "undefined") {
          commit("SET_ERRORS", "email");
          commit("SET_MENSAJE_LOGIN", res.response.data.errors.email[0]);
        }
        if (typeof err_destiempo !== "undefined") {
          commit("SET_ERRORS", "destiempo");
        }
        if (res.response?.data?.error === "mail_failed") {
          commit("SET_ERRORS", "mail_failed");
          commit(
            "SET_MENSAJE_LOGIN",
            res.response.data.message || "No se pudo enviar el correo de verificación."
          );
        }
        return { requires_2fa: false, error: true };
      }
    },

    /** Paso 2: verifica OTP de 7 dígitos y abre sesión (cookie Sanctum). */
    async verify2fa({ dispatch }, payload) {
      try {
        await axios.get("/sanctum/csrf-cookie");
        await axios.post("/login/2fa/verify", payload);
        await dispatch("setDarkMode", false);
        await dispatch("getUser");
        return { ok: true };
      } catch (error) {
        const data = error.response?.data || {};
        return {
          ok: false,
          error: data.error || "verify_failed",
          message: data.message || "No se pudo verificar el código.",
          attempts_remaining: data.attempts_remaining,
        };
      }
    },

    /** Reenvía OTP; puede devolver un challenge_id nuevo. */
    async resend2fa(_, payload) {
      try {
        await axios.get("/sanctum/csrf-cookie");
        const res = await axios.post("/login/2fa/resend", payload);
        return { ok: true, ...res.data };
      } catch (error) {
        const data = error.response?.data || {};
        return {
          ok: false,
          error: data.error || "resend_failed",
          message: data.message || "No se pudo reenviar el código.",
          retry_after: data.retry_after,
        };
      }
    },

    async logout({ commit, dispatch }) {
      try {
        await axios.get("/logout");
      } catch (error) {
        // Continuar incluso si el backend falla
      }
      
      // Limpiar estado de Vuex completamente
      commit("SET_USER", null);
      commit("SET_PERMISSIONS", []);
      commit("SET_MENUS", []);
      commit("SET_ROUTES", []);
      commit("SET_ERRORS", null);
      commit("SET_MENSAJE_LOGIN", null);
      
      // Limpiar localStorage completamente
      localStorage.clear();
      sessionStorage.clear();
      
      // Redirigir al login solo si no estamos ya en la página de login
      if (router.currentRoute.path !== '/login') {
        router.push('/login').catch(() => {
          // Manejar error de navegación duplicada silenciosamente
        });
      }
      
      return true;
    },

    async getUser({ commit, dispatch }) {
      try {
        const res = await axios.get("/api/user");
        commit("SET_USER", res.data);
        
        // Solo si el usuario se carga exitosamente, cargar permissions y menus
        await Promise.all([
          dispatch("getPermissions"),
          dispatch("getMenus")
        ]);
        
        // Solo después cargar las rutas
        await dispatch("getRoutes", this.getters.getMenus);
        await dispatch("setRoutes");
        
      } catch (err) {
        commit("SET_USER", null);
        commit("SET_PERMISSIONS", []);
        commit("SET_MENUS", []);
        commit("SET_ROUTES", []);
        
        // Re-lanzar el error para que el caller pueda manejarlo
        throw err;
      }
    },

    async getPermissions({ commit }) {
      await axios
        .get("/api/user/permissions")
        .then((res) => {
          commit("SET_PERMISSIONS", res.data);
        })
        .catch((err) => {
          commit("SET_PERMISSIONS", []);
        });
    },

    async getMenus({ commit }) {
      await axios
        .get("/api/menu")
        .then((response) => {
          commit("SET_MENUS", response.data);
        })
        .catch((err) => {
          commit("SET_MENUS", []);
        });
    },

    async getRoutes({ commit }, menus) {
      var r = [];
      menus.forEach((menu) => {
        menu.nivel2.forEach((nivel2) => {
          nivel2.rutas.forEach((ruta) => {
            r.push(ruta);
          });
        });
      });
      commit("SET_ROUTES", r);
    },

    async setRoutes() {
      this.getters.getRoutes.forEach((dataRoutes) => {
        var newRoute = {
          path: dataRoutes.path,
          component: () => import("../views/" + dataRoutes.component + ".vue"),
          name: dataRoutes.name,
        };
        router.addRoutes([newRoute]);
      });
    },

    async setDarkMode({ commit }, darkMode) {
      commit("SET_DARK_MODE", darkMode);
    },
  },
  modules: {
    modSeguridad: modSeguridad,
  },
});
